import { NewCard } from "@components/Cards/Templates/newCard";

import DepartmentModal from "@components/Modals/DepartmentModal";
import BroadcastGroupModal from "@components/Modals/BroadcastGroupModal";

import { ConfirmCancelButtons } from "@components/Buttons/ConfirmCancelButtons";

import LoadingIcon from "@common/LoadingIcon";
import PasswordResetModal from "@components/Modals/PasswordResetModal";
import { sortApps } from "@services/Utilities";
import { error_message } from "@constants/Errors";
import messages from "@constants/Messages";
import WarningModal from "@components/Modals/WarningModal";
import { UserNameCard } from "@components/Forms/UserProfileForm/components/UserNameCard";
import { UserAccountInfoCard } from "@components/Forms/UserProfileForm/components/UserAccountInfoCard";
import { UserStatusCard } from "@components/Forms/UserProfileForm/components/UserStatusCard";
import { UserDepartmentPositionCard } from "@components/Forms/UserProfileForm/components/UserDepartmentPositionCard";
import { UserYobelyInfoCard } from "@components/Forms/UserProfileForm/components/UserYobelyInfoCard";
import { UserLoginInfoCard } from "@components/Forms/UserProfileForm/components/UserLoginInfoCard";
import { UserAccessInfoCard } from "@components/Forms/UserProfileForm/components/UserAccessInfoCard";
import { UserProfileImageCard } from "@components/Forms/UserProfileForm/components/UserProfileImageCard";
import { useUserProfileForm } from "@components/Forms/UserProfileForm/hooks/useUserProfileForm";
import { UserCompanyInfoCard } from "@components/Forms/UserProfileForm/components/UserCompanyInfoCard";
import { UserAppsInfoCard } from "@components/Forms/UserProfileForm/components/UserAppsInfoCard";
import { ROLES } from "@constants/Enums";
import { UserOtherInformationCard } from "@components/Forms/UserProfileForm/components/UserOtherInformationCard";
import DuplicateUserModal from "@components/Modals/DuplicateUsersModal";
import { UserKeycloakGroupInfoCard } from "./components/UserKeycloakGroupInfoCard";
import { useEffect } from "react";

interface UserInformationProps {
  id: string;
}

const UserProfileForm: React.FC<UserInformationProps> = ({ id }) => {
  const form = useUserProfileForm(id);

  return (
    <div>
      {form.loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <LoadingIcon />
        </div>
      ) : form.userData ? (
        <form
          onSubmit={(e) => {
            form.createUser ? form.createNewUser(e, false) : form.handlePut(e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <div className="flex flex-row justify-end">
            {form.error && !form.loading && (
              <button
                className="button-default"
                type="button"
                onClick={form.handleOpenWarningModal}
              >
                {messages.generic.show_error}
              </button>
            )}
          </div>

          <NewCard
            header={"基本情報"}
            bodyLabel={""}
            loading={form.loading}
            bodyContents={
              <div>
                {!form.isAdminLoggedIn && (
                  <>
                    <UserProfileImageCard
                      previews={form.previews}
                      handleFileChange={form.handleFileChange}
                      handleFileDelete={form.handleFileDelete}
                      imageLoading={form.imageLoading}
                      handleImageLoaded={form.handleImageLoaded}
                      fileInput={form.fileInput}
                    ></UserProfileImageCard>

                    <UserAccessInfoCard
                      currentUserRole={form.currentUserRole}
                      roles={form.roles}
                      handleDropdownChange={form.handleDropdownChange}
                      roleError={form.roleError}
                      apps={form.apps}
                      selectedApps={form.selectedApps}
                      handleCheckboxChange={form.handleCheckboxChange}
                      sortApps={sortApps}
                      error_message={error_message}
                      createUser={form.createUser}
                    ></UserAccessInfoCard>

                    {form.currentUserRole?.name !== ROLES.CONTACT && (
                      <UserAppsInfoCard
                        apps={form.apps}
                        selectedApps={form.selectedApps}
                        handleCheckboxChange={form.handleCheckboxChange}
                        sortApps={sortApps}
                        error_message={error_message}
                      />
                    )}
                  </>
                )}

                {(form.currentUserRole?.name === ROLES.ADMIN ||
                  form.currentUserRole?.name === ROLES.GENERAL) &&
                  !form.createUser && (
                    <div className="mt-5">
                      <UserKeycloakGroupInfoCard
                        tenantKeycloakGroups={
                          form.userData.tenant_keycloak_groups
                        }
                        userKeycloakGroups={form.keycloakGroups}
                        handleCheckboxChange={
                          form.handleKeycloakGroupsCheckboxChange
                        }
                        hasKakelyLicense={form.hasKakelyLicense}
                        hasYobelyLicense={form.hasYobelyLicense}
                        error_message={error_message}
                      />
                    </div>
                  )}

                <div className="mt-5">
                  <UserNameCard
                    userData={form.userData}
                    errors={form.errors}
                    updateUserData={form.updateUserData}
                    updateError={form.updateError}
                  />

                  {!form.isAdminLoggedIn && (
                    <>
                      <UserAccountInfoCard
                        userData={form.userData}
                        errors={form.errors}
                        updateUserData={form.updateUserData}
                        updateError={form.updateError}
                        adminRoleSelected={form.adminRoleSelected}
                        hasKakelyLicense={form.hasKakelyLicense}
                        hasYobelyLicense={form.hasYobelyLicense}
                        contactRoleSelected={form.contactRoleSelected}
                        createUser={form.createUser}
                      />
                    </>
                  )}

                  {form.currentUserRole?.name !== ROLES.CONTACT && (
                    <>
                      <UserLoginInfoCard
                        userData={form.userData}
                        errors={form.errors}
                        showPassword={form.showPassword}
                        password={form.userData.login_password || ""}
                        setShowPassword={form.setShowPassword}
                        updateUserData={form.updateUserData}
                        updateError={form.updateError}
                        handleOpenPasswordModal={form.handleOpenPasswordModal}
                        createUser={form.createUser}
                      ></UserLoginInfoCard>
                      <div className="mt-5 divider"></div>
                    </>
                  )}

                  {!form.isAdminLoggedIn && (
                    <>
                      <UserCompanyInfoCard
                        userData={form.userData}
                        updateUserData={form.updateUserData}
                        errors={form.errors}
                        updateError={form.updateError}
                      />
                      <div className="mt-5 divider"></div>
                      <h2 className="mb-5 label-light-blue-xl"> 所属部署 </h2>

                      <div>
                        <UserDepartmentPositionCard
                          departmentName={
                            form.tenantDepartmentData?.department_name
                          }
                          positionName={
                            form.tenantDepartmentData?.position_name
                          }
                          errors={form.errors}
                          updateTenantDepartmentData={
                            form.updateTenantDepartmentData
                          }
                          updateError={form.updateError}
                          handleDepartmentClick={form.handleDepartmentClick}
                        />
                      </div>

                      {form.hasYobelyLicense && (
                        <div>
                          <UserYobelyInfoCard
                            createUser={form.createUser}
                            broadcastGroupName={form.broadcastGroup?.name}
                            handleBroadcastGroupModalClick={
                              form.handleBroadcastGroupModalClick
                            }
                          ></UserYobelyInfoCard>
                        </div>
                      )}

                      <UserOtherInformationCard
                        userInfo={form.userData}
                        onValueChange={form.updateUserData}
                      />

                      {form.currentUserRole?.name !== ROLES.CONTACT && (
                        <>
                          <div className="mt-5 divider"></div>
                          <UserStatusCard
                            status={form.userData?.is_display}
                            onStatusChange={(value) =>
                              form.updateUserData("is_display", value)
                            }
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            }
          ></NewCard>
          <ConfirmCancelButtons
            confirmText={"更新する"}
            form={true}
            onCancel={() => {
              form.navigate("/users");
            }}
          ></ConfirmCancelButtons>
        </form>
      ) : (
        form.userNotFound && (
          <p className="not-found-text">
            {form.myUserPage
              ? error_message.user.not_found
              : error_message.user.not_found_id(id)}
          </p>
        )
      )}

      <DuplicateUserModal
        title={`${form.dupErrorType}が重複しています。`}
        type={form.dupErrorType}
        isOpen={form.duplicateExistsModal}
        onClose={() => form.setDuplicateExistsModal(false)}
        duplicateUsers={form.duplicateUsers}
        onCreateUser={(e) => form.handlePostUserData(e, true)}
        onUpdateUser={(e, id) => form.handleUpdateDuplicateUser(e, id, true)}
        showEditOptions={true}
      />

      <PasswordResetModal
        isOpen={form.passwordChangeModal}
        onRequestClose={form.handleClosePasswordModal}
        onPasswordResetSuccess={form.submitPasswordReset}
      ></PasswordResetModal>
      <BroadcastGroupModal
        isOpen={form.broadcastGroupModalOpen}
        onRequestClose={form.handleBroadcastGroupModalClose}
        onSelectGroup={form.handleSelectBroadcastGroup}
      />
      <DepartmentModal
        isOpen={form.departmentModalOpen}
        title={form.departmentModalTitle}
        onRequestClose={form.handleDepartmentModalClose}
        onSelectGroup={form.handleSelectDepartment}
      />
      <WarningModal
        title={messages.generic.error_modal_title}
        body={form.error}
        confirmText={""}
        cancelText="戻る"
        isOpen={form.warningModalOpen}
        onRequestClose={form.handleCloseWarningModal}
      ></WarningModal>
    </div>
  );
};

export default UserProfileForm;
